import type { AirportData, EnvState } from '@atc/core';
import type { Sim } from '@atc/sim';
import type { CameraCommand, QualityLevel, TowerView } from '@atc/tower-view';

export interface GameTower {
  readonly view: TowerView | null;
  setVisible(visible: boolean): void;
  setQuality(quality: QualityLevel | 'auto'): void;
  setCamera(command: CameraCommand): void;
  showImpact(position: { lat: number; lon: number; altitudeFt: number }): void;
  tick(dt: number): void;
  destroy(): void;
}

/** Owns the lazy Three.js import and every resource created by the tower panel. */
export function createGameTower(options: {
  panel: HTMLElement;
  container: HTMLElement;
  airport: AirportData;
  sim: Sim;
  quality: QualityLevel | 'auto';
  /** The session's time of day and weather, read on every update. */
  environment(): EnvState;
  onSelect(callsign: string): void;
  onError(message: string): void;
}): GameTower {
  let view: TowerView | null = null;
  let wanted = false;
  let destroyed = false;
  let accumulator = 0;

  const environment = options.environment;

  const close = () => {
    wanted = false;
    view?.destroy();
    view = null;
    options.panel.hidden = true;
  };

  const open = async () => {
    if (wanted || destroyed) return;
    wanted = true;
    options.panel.hidden = false;
    try {
      const tower = await import('@atc/tower-view');
      if (destroyed || !wanted || view) return;
      const imagery = options.airport.icao.startsWith('K') || options.airport.icao.startsWith('P') ? 'usgs' : null;
      const preset = imagery ? tower.IMAGERY_PRESETS[imagery] : { url: '', attribution: 'Bundled CC0 ground textures. Elevation: Mapzen/AWS Terrain Tiles.' };
      const folder = options.airport.icao.toLowerCase();
      view = tower.createTowerView(options.container, {
        airportUrl: `/data/${folder}/airport.json`,
        osmUrl: `/data/${folder}/osm.geojson`,
        terrain: { imageryUrl: preset.url, imageryAttribution: preset.attribution },
        quality: options.quality,
        showStats: false,
        tower: options.airport.xAtc.tower
          ? { lat: options.airport.xAtc.tower.lat, lon: options.airport.xAtc.tower.lon, eyeHeightAglM: options.airport.xAtc.tower.eyeHeightAglM }
          : undefined,
      });
      view.on('select', (callsign) => { if (callsign) options.onSelect(callsign); });
      view.update(options.sim.aircraft(), environment(), options.sim.simTimeS);
      const opening = view;
      opening.ready.catch((error) => {
        if (!destroyed && view === opening) {
          options.onError(`Tower view: ${error}`);
          close();
        }
      });
    } catch (error) {
      options.onError(`Tower view failed to load: ${error}`);
      close();
    }
  };

  return {
    get view() { return view; },
    setVisible(visible) { if (visible) void open(); else close(); },
    setQuality(quality) { options.quality = quality; view?.setQuality(quality); },
    setCamera(command) { view?.setCamera(command); },
    showImpact(position) { view?.showImpact(position); },
    tick(dt) {
      if (!view) return;
      accumulator += dt;
      if (accumulator < 1) return;
      accumulator = 0;
      view.update(options.sim.aircraft(), environment(), options.sim.simTimeS);
    },
    destroy() { destroyed = true; close(); },
  };
}
